import { useLingui } from '@lingui/react/macro'
import { Link } from '@tanstack/react-router'
import { DropdownMenuItem } from 'designSystem/components/DropdownMenu'
import { sessionRecordingControl } from 'features/analytics/sessionRecordingControl'
import { MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST } from 'features/manageUsers'
import { PermissionsGate } from 'features/permissions'
import React from 'react'
import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { createUsersSearch } from 'routes/users/-searchParams'
import {
  PERMISSION_GECKO_COST_METRICS_VIEW,
  PERMISSION_GECKO_INTERNAL_MEMBER,
} from 'types'

import {
  type ConfigSettingsProps,
  type ConfigSettingsState,
} from './useConfigSettingsState'
import { WORKSPACE_SETTINGS_DESTINATION } from './workspaceSettingsLink'

export type WorkspaceSettingsNavigateHandler = (
  event: React.MouseEvent<HTMLAnchorElement>,
  info: { label: string; destinationKey: string }
) => void

export function ConfigSettingsSidebarMenuItems({
  orgID,
  siteID,
  state,
  onWorkspaceNavigate,
}: ConfigSettingsProps & {
  state: ConfigSettingsState
  onWorkspaceNavigate: WorkspaceSettingsNavigateHandler
}) {
  const { t } = useLingui()
  const linkClick =
    (label: string, destinationKey: string) =>
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      onWorkspaceNavigate(event, { label, destinationKey })
    }

  return (
    <>
      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownMenuItem asChild>
          <Link
            to='/app-config'
            onClick={linkClick(
              t`Org & Site App Config`,
              WORKSPACE_SETTINGS_DESTINATION.appConfig
            )}>
            {t`Org & Site App Config`}
          </Link>
        </DropdownMenuItem>
      </PermissionsGate>

      {orgID && (
        <PermissionsGate requires={MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST}>
          <DropdownMenuItem asChild>
            <Link
              to='/users'
              search={createUsersSearch({ orgId: orgID, siteId: siteID })}
              onClick={linkClick(
                t`Manage Users`,
                WORKSPACE_SETTINGS_DESTINATION.manageUsers
              )}>
              {t`Manage Users`}
            </Link>
          </DropdownMenuItem>
        </PermissionsGate>
      )}

      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownMenuItem asChild>
          <Link
            to='/admin/users'
            onClick={linkClick(
              t`Manage Permissions`,
              WORKSPACE_SETTINGS_DESTINATION.managePermissions
            )}>
            {t`Manage Permissions`}
          </Link>
        </DropdownMenuItem>
      </PermissionsGate>

      {state.isSessionRecordingOrg && (
        <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault()
              state.setRecordingOn(sessionRecordingControl.toggle())
            }}>
            {state.recordingOn
              ? t`Session recording: On`
              : t`Session recording: Off (this browser)`}
          </DropdownMenuItem>
        </PermissionsGate>
      )}

      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownMenuItem asChild>
          <Link
            to='/health-check'
            onClick={linkClick(
              t`Health Check`,
              WORKSPACE_SETTINGS_DESTINATION.healthCheck
            )}>
            {t`Health Check`}
          </Link>
        </DropdownMenuItem>
      </PermissionsGate>

      <PermissionsGate requires={[PERMISSION_GECKO_COST_METRICS_VIEW]}>
        <DropdownMenuItem asChild>
          <Link
            to='/cost-dashboard'
            onClick={linkClick(
              t`Cost Dashboard`,
              WORKSPACE_SETTINGS_DESTINATION.costDashboard
            )}>
            {t`Cost Dashboard`}
          </Link>
        </DropdownMenuItem>
      </PermissionsGate>

      {orgID && (
        <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
          <DropdownMenuItem asChild>
            <Link
              to={'/extensions'}
              search={(prev) => ({
                ...prev,
                [SEARCH_PARAM_ORG_ID]: orgID,
                [SEARCH_PARAM_SITE_ID]: siteID,
              })}
              onClick={linkClick(
                t`Manage Extensions`,
                WORKSPACE_SETTINGS_DESTINATION.manageExtensions
              )}>
              {t`Manage Extensions`}
            </Link>
          </DropdownMenuItem>
        </PermissionsGate>
      )}
    </>
  )
}
